import React, { useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';

import {
    AddCircledIcon,
    Button,
    Callout,
    FlexContainer,
    IconButton,
    Link,
    MenuText,
    TrashIcon,
    UtilityInfoIcon,
} from '@placer-ui/components';
import { ControlledInputField } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-input-field';
import type { RequestMarketFormProps } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-market/types';
import { ControlledBasicSingleSelect } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-basic-single-select';
import {
    coveredMarketCategories,
    marketCategories,
    statesItems,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-market/consts';
import { goToInANewTab } from 'router/go-to';
import { getFeatureFlags } from 'core/flow-control';
import styles from './request-market.module.scss';
import {
    SupportOpportunityUrlInput,
    SupportSalesEmailInput,
} from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-support-sales-email-inputs/support-sales-email-inputs';
const MAX_NAME_LENGTH = 100;
const MAX_INPUT_LENGTH = 150;
const MAX_PROPERTIES = 20;

const emptyMarketForm = {
    name: '',
};

export const ManualRequestMarket = ({ formControl }: RequestMarketFormProps) => {
    const { enable_request_properties_sales_flow_ff } = getFeatureFlags();

    const marketsArray = useFieldArray({
        control: formControl.control,
        name: 'markets',
    });

    const { id, ...market } = marketsArray.fields[0];

    const disableRemoveButton = useMemo(
        () =>
            marketsArray.fields.length === 1 &&
            JSON.stringify(market) === JSON.stringify(emptyMarketForm),
        [marketsArray.fields.length, market],
    );

    const category = formControl.watch('category');

    const showState = category !== 'state';
    const showCounty = showState && category !== 'county';
    const showCity = showCounty && category !== 'city';

    const notedCategory = coveredMarketCategories[category];

    return (
        <div className={styles.marketManualForm}>
            <div className={styles.formContainer}>
                {notedCategory && (
                    <div className={styles.callout}>
                        <Callout
                            FreeTextIcon={<UtilityInfoIcon />}
                            text={`Placer already covers ${notedCategory}. Please check`}
                            CTA={
                                <>
                                    <Link target="_blank" onClick={() => goToInANewTab('markets')}>
                                        Market Section
                                    </Link>
                                    &nbsp; first before proceeding.
                                </>
                            }
                        />
                    </div>
                )}
                {enable_request_properties_sales_flow_ff && (
                    <FlexContainer gap={16}>
                        <SupportSalesEmailInput />
                        <SupportOpportunityUrlInput />
                    </FlexContainer>
                )}
                <div className={styles.inputsSection}>
                    <MenuText variant="semi-bold">Market Boundaries</MenuText>
                    <div className={styles.inputsLine}>
                        <div className={styles.inputWrapper}>
                            <ControlledBasicSingleSelect
                                name="category"
                                testId="markets-category"
                                placeholder="Category (optional)"
                                control={formControl.control}
                                items={marketCategories}
                                withInlineSearch
                                showClearIndicator
                            />
                        </div>
                    </div>
                    <div className={styles.inputsLine}>
                        {showCity && (
                            <div className={styles.inputWrapper}>
                                <ControlledInputField
                                    testId="markets-city"
                                    placeholder="City (optional)"
                                    control={formControl.control}
                                    name="city"
                                    maxLength={MAX_INPUT_LENGTH}
                                />
                            </div>
                        )}
                        {showCounty && (
                            <div className={styles.inputWrapper}>
                                <ControlledInputField
                                    testId="markets-county"
                                    placeholder="County (optional)"
                                    control={formControl.control}
                                    name="county"
                                    maxLength={MAX_INPUT_LENGTH}
                                />
                            </div>
                        )}
                        {showState && (
                            <div className={styles.inputWrapper}>
                                <ControlledBasicSingleSelect
                                    name="state"
                                    testId="markets-state"
                                    placeholder="State (optional)"
                                    control={formControl.control}
                                    items={statesItems}
                                    withInlineSearch
                                    showClearIndicator
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.inputsSection}>
                    <MenuText variant="semi-bold">Market Name</MenuText>
                    {marketsArray.fields.map((property, index) => (
                        <div className={styles.inputsLine} key={property.id}>
                            <ControlledInputField
                                testId={`market-${index}-name`}
                                placeholder="Market name"
                                control={formControl.control}
                                name={`markets.${index}.name`}
                                maxLength={MAX_NAME_LENGTH}
                                rules={{
                                    required: { value: true, message: 'This field is required' },
                                }}
                            />
                            <IconButton
                                disabled={disableRemoveButton}
                                Icon={<TrashIcon />}
                                variant="secondary-gray"
                                testId={`remove-market-button-${index}`}
                                onClick={() => {
                                    if (marketsArray.fields.length === 1) {
                                        marketsArray.append(emptyMarketForm);
                                    }
                                    marketsArray.remove(index);
                                }}
                                aria-label="Remove Market"
                            />
                        </div>
                    ))}
                    {marketsArray.fields.length < MAX_PROPERTIES && (
                        <Button
                            LeftIcon={<AddCircledIcon />}
                            variant="secondary-blue"
                            onClick={() => marketsArray.append(emptyMarketForm)}
                            testId="add-market-button"
                        >
                            Add Market
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
